import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, CheckCircle2, AlertTriangle, Repeat, Timer } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { businessApi, fxApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import NlpComposer from "../../components/NlpComposer";
import { toast } from "sonner";

const CURRENCIES = ["NEO", "USD", "EUR", "GBP", "INR", "JPY", "AED"];

const fmtNum = (s) => {
  try {
    const n = Number(s);
    if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
  } catch {}
  return s;
};

export default function SendPage() {
  const { business } = useAuth();
  const nav = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [toVpa, setToVpa] = useState("");
  const [amount, setAmount] = useState("");
  const [basis, setBasis] = useState("send"); // send | receive
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);

  const [resolved, setResolved] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isCrossCurrency = !!toCurrency && !!fromCurrency && toCurrency !== fromCurrency;

  useEffect(() => {
    businessApi.wallets().then((w) => setWallets(w));
  }, []);

  // Default toCurrency to fromCurrency once selected
  useEffect(() => {
    if (fromCurrency && !toCurrency) setToCurrency(fromCurrency);
  }, [fromCurrency]); // eslint-disable-line

  // Resolve VPA
  useEffect(() => {
    if (!toVpa || toVpa.length < 2) {
      setResolved(null);
      setResolveError(null);
      return;
    }
    const t = setTimeout(async () => {
      setResolving(true);
      setResolveError(null);
      try {
        const r = await businessApi.resolveVpa(toVpa);
        setResolved(r);
      } catch (e) {
        setResolved(null);
        setResolveError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setResolving(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [toVpa]);

  // Fetch / refresh quote whenever inputs change (cross-currency only)
  const fetchTimer = useRef(null);
  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    if (!isCrossCurrency || !amount || Number(amount) <= 0) return;
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const body = {
          from_currency: fromCurrency,
          to_currency: toCurrency,
          basis,
        };
        if (basis === "send") body.from_amount = amount;
        else body.to_amount = amount;
        const q = await fxApi.quote(body);
        setQuote(q);
        setQuoteError(null);
      } catch (e) {
        setQuote(null);
        setQuoteError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setQuoteLoading(false);
      }
    }, 400);
    return () => fetchTimer.current && clearTimeout(fetchTimer.current);
  }, [fromCurrency, toCurrency, amount, basis, isCrossCurrency]);

  // Quote countdown — re-fetch when expiring
  useEffect(() => {
    if (!quote) return;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(quote.expires_at).getTime() - Date.now()) / 1000)
      );
      setSecondsLeft(remaining);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [quote]);

  // Auto-refresh expired quote
  useEffect(() => {
    if (quote && secondsLeft === 0 && !confirmOpen) {
      // trigger fresh fetch by toggling basis & restoring (forces effect)
      const refresh = async () => {
        try {
          const body = {
            from_currency: fromCurrency,
            to_currency: toCurrency,
            basis,
          };
          if (basis === "send") body.from_amount = amount;
          else body.to_amount = amount;
          const q = await fxApi.quote(body);
          setQuote(q);
        } catch {}
      };
      refresh();
    }
  }, [secondsLeft]); // eslint-disable-line

  const sourceWallet = wallets.find((w) => w.currency === fromCurrency);

  const recipientSupports =
    !isCrossCurrency
      ? !!resolved // same-ccy needs only existence
      : !!resolved &&
        resolved.supported_currencies?.includes(toCurrency);

  const canSend = !!fromCurrency && !!toCurrency && !!resolved && recipientSupports &&
    !!amount && Number(amount) > 0 &&
    (!isCrossCurrency || (!!quote && !quoteError && secondsLeft > 0)) &&
    !busy;

  const onSubmit = async () => {
    setBusy(true);
    try {
      let result;
      if (isCrossCurrency) {
        result = await businessApi.transfer({
          to_vpa: resolved.vpa,
          quote_id: quote.quote_id,
          memo: memo || null,
        });
      } else {
        result = await businessApi.transfer({
          to_vpa: resolved.vpa,
          currency: fromCurrency,
          amount,
          memo: memo || null,
        });
      }
      setConfirmOpen(false);

      if (result?.status === "held") {
        toast.warning("Held for review", {
          description: `Risk ${result.verdict?.final_score}/100 — open Holds to approve.`,
        });
        nav("/app/holds");
        return;
      }
      const legs = result?.transactions || [];
      toast.success("Transfer submitted", {
        description: `${legs.length} leg(s) pending — confirms on next block (~2s).`,
      });
      if (legs[0]) nav(`/tx/${legs[0].tx_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "BLOCKED_BY_RISK") {
        toast.error("Blocked by risk engine", { description: detail.message });
      } else {
        toast.error("Send failed", { description: formatApiError(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Send</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <Send className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          Send to a VPA
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Same-currency settles instantly. Cross-currency routes through NEO as bridge asset
          with a 60-second quote.
        </p>
      </div>

      <div className="mb-8">
        <NlpComposer
          onExecuted={(r) => nav(`/tx/${r.transactions?.[0]?.tx_id}`)}
          onHeld={() => nav("/app/holds")}
          onScheduled={() => nav("/app/scheduled")}
        />
      </div>

      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Or use the form</div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) setConfirmOpen(true);
        }}
        className="space-y-5"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
            <Select value={fromCurrency} onValueChange={setFromCurrency}>
              <SelectTrigger data-testid="send-from-currency" className="rounded-sm font-mono mt-1">
                <SelectValue placeholder="Pick" />
              </SelectTrigger>
              <SelectContent>
                {wallets.map((w) => (
                  <SelectItem key={w.currency} value={w.currency} data-testid={`send-from-${w.currency}`}>
                    {w.currency} — {fmtNum(w.balance)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sourceWallet && (
              <div className="text-[11px] font-mono text-muted-foreground mt-1">
                Avail: {fmtNum(sourceWallet.balance)} {fromCurrency}
              </div>
            )}
          </div>

          <div className="md:col-span-1 flex items-end justify-center pb-2">
            <Repeat className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          </div>

          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">To currency</Label>
            <Select value={toCurrency} onValueChange={setToCurrency}>
              <SelectTrigger data-testid="send-to-currency" className="rounded-sm font-mono mt-1">
                <SelectValue placeholder="Pick" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`send-to-ccy-${c}`}>
                    {c}{c === fromCurrency ? " (same)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">To VPA</Label>
            <Input
              data-testid="send-to-vpa"
              value={toVpa}
              onChange={(e) => setToVpa(e.target.value.toLowerCase())}
              placeholder={business ? `globex@${business.full_vpa.split("@")[1]}` : "globex@neopay"}
              className="rounded-sm font-mono mt-1"
            />
            <div className="mt-2 min-h-[20px]">
              {resolving && <span className="text-[11px] text-muted-foreground">Resolving…</span>}
              {resolved && !resolving && (
                <span data-testid="vpa-resolved" className="text-[11px] inline-flex items-center gap-1.5 text-status-ok">
                  <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                  <span className="font-medium">{resolved.legal_name}</span>
                  <span className="text-muted-foreground">supports {resolved.supported_currencies.join(", ")}</span>
                </span>
              )}
              {resolveError && !resolving && (
                <span data-testid="vpa-error" className="text-[11px] text-status-tampered inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> {resolveError}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Basis</Label>
            <Select value={basis} onValueChange={setBasis} disabled={!isCrossCurrency}>
              <SelectTrigger data-testid="send-basis" className="rounded-sm font-mono mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="send">Send {fromCurrency || "amount"}</SelectItem>
                <SelectItem value="receive">Recipient gets {toCurrency || "amount"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount</Label>
            <Input
              type="number" min="0" step="0.000001"
              data-testid="send-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-sm font-mono mt-1"
              placeholder="0.00"
            />
          </div>
          <div className="md:col-span-6">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Memo</Label>
            <Input
              data-testid="send-memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="rounded-sm font-mono mt-1"
              placeholder="Invoice #1234"
              maxLength={100}
            />
          </div>
        </div>

        {/* Live quote preview (cross-currency) */}
        {isCrossCurrency && (
          <div data-testid="quote-preview" className="border border-border rounded-sm bg-card p-4">
            {quoteLoading && !quote && <div className="text-xs text-muted-foreground">Fetching quote…</div>}
            {quoteError && <div className="text-xs text-status-tampered">{quoteError}</div>}
            {quote && (
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Live quote</div>
                <div className="font-mono text-lg leading-relaxed">
                  Send <span className="font-medium">{fmtNum(quote.from_amount)} {quote.from_currency}</span>
                  {" → "}
                  Recipient gets <span className="font-medium text-status-ok">{fmtNum(quote.to_amount)} {quote.to_currency}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono text-muted-foreground">
                  <div>NEO bridge: {fmtNum(quote.neo_intermediate)}</div>
                  <div>Spread: {fmtNum(quote.spread_neo)} NEO ({quote.spread_bps} bps)</div>
                  <div>NEO/USD: {quote.neo_base_usd}</div>
                  <div className="inline-flex items-center gap-1">
                    <Timer className="w-3 h-3" strokeWidth={1.5} />
                    valid <span data-testid="quote-countdown" className={secondsLeft < 10 ? "text-status-tampered" : ""}>{secondsLeft}s</span>
                  </div>
                </div>
                {quote.stale && (
                  <div className="text-[11px] text-status-tampered">
                    ⚠ Rates are stale (last fetch failed). Treat with caution.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isCrossCurrency && resolved && !recipientSupports && (
          <div data-testid="recipient-no-ccy" className="border border-status-tampered/40 bg-status-tampered/10 p-3 rounded-sm text-status-tampered text-xs">
            <strong>{resolved.legal_name}</strong> does not have a {toCurrency} wallet — cannot receive in this currency.
          </div>
        )}

        <Button type="submit" disabled={!canSend} data-testid="send-submit" className="rounded-sm">
          Review &amp; send
        </Button>
      </form>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Confirm transfer</DialogTitle>
            <DialogDescription>
              {isCrossCurrency
                ? "This is an atomic 4-leg cross-currency bundle. All legs settle in the same block or none."
                : "Same-currency same-block transfer. This cannot be reversed."}
            </DialogDescription>
          </DialogHeader>
          {resolved && (
            <div className="space-y-2 font-mono text-xs">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">From</span>
                <span className="col-span-2">{business?.legal_name} ({fromCurrency})</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">To</span>
                <span className="col-span-2">{resolved.legal_name} ({resolved.vpa})</span>
              </div>
              {!isCrossCurrency ? (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="col-span-2">{fmtNum(amount)} {fromCurrency}</span>
                </div>
              ) : quote && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">Send</span>
                    <span className="col-span-2">{fmtNum(quote.from_amount)} {quote.from_currency}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">Receives</span>
                    <span className="col-span-2 text-status-ok">{fmtNum(quote.to_amount)} {quote.to_currency}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">Spread</span>
                    <span className="col-span-2">{fmtNum(quote.spread_neo)} NEO</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">Quote valid</span>
                    <span className="col-span-2">{secondsLeft}s</span>
                  </div>
                </>
              )}
              {memo && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Memo</span>
                  <span className="col-span-2 break-words">{memo}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={onSubmit} disabled={busy} data-testid="send-confirm" className="rounded-sm">
              {busy ? "Sending…" : "Confirm send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
